/*
 * useUEContext — the single source of truth for Universal Editor host state
 * in the Demo Pilot rail.
 *
 * Attaches once to the UE host via @adobe/uix-guest, reads sharedContext for
 * auth + org id, reads editorState for connections (author URL), editables
 * (site slug inference) and selection. Polls editorState every 1500ms like
 * the reference repo (Ref-Demo-Image-Generation-Extn) does.
 *
 * Returns a stable object shape; all fields may be empty strings or null
 * before the host connection is ready or while running outside UE.
 */

import { useEffect, useRef, useState } from 'react';
import { attach } from '@adobe/uix-guest';
import { extensionId } from '../components/Constants';
import {
  siteSlugFromEditables,
  siteSlugFromResource,
  siteSlugFromLocation,
  damFolderFromSiteSlug,
  isImageLikeResource,
  stripResourceUrn,
  pagePathFromLocation,
  pagesRootFromPagePath,
  pagesRootFromEditables,
} from '../lib/paths';

const POLL_MS = 1500;
const ATTACH_RETRIES = 5;
const ATTACH_RETRY_DELAY_MS = 400;

function pickAuthorUrlFromConnections(connections) {
  if (!connections || typeof connections !== 'object') return '';
  const values = Object.values(connections);
  // 1) Direct string value
  const strVal = values.find((v) => typeof v === 'string' && v);
  if (strVal) return strVal;
  // 2) Object with a .url string
  const objVal = values.find((v) => v && typeof v === 'object' && typeof v.url === 'string' && v.url);
  if (objVal) return objVal.url;
  return '';
}

function normalizeAuthorUrl(raw) {
  if (!raw || typeof raw !== 'string') return '';
  // UE sometimes prefixes connection URLs with `aem:` or `xwalk:`
  const stripped = raw.replace(/^(aem:|xwalk:)/, '');
  if (stripped.startsWith('http://') || stripped.startsWith('https://')) return stripped;
  return `https://${stripped}`;
}

function readSelectionFromState(editorState) {
  const selectedMap = editorState?.selected || {};
  const editables = editorState?.editables || [];
  const selectedId = Object.keys(selectedMap).find((id) => selectedMap[id]) || '';
  if (!selectedId) {
    return { selectedId: '', resource: '', label: '', imageLike: false, editable: null };
  }
  const selectedEditable = editables.find((e) => e && e.id === selectedId);
  const label =
    selectedEditable?.name || selectedEditable?.title || selectedEditable?.label
    || selectedEditable?.resource || selectedEditable?.type || selectedId;

  // Prefer the selected editable's resource; if absent, walk backwards through
  // earlier editables for the nearest one with a resource path. We track BOTH
  // the resource string and the editable object that owns it — editorActions
  // .details() / .update() expect the editable, not the URN.
  let resource = '';
  let resourceEditable = selectedEditable || null;
  if (typeof selectedEditable?.resource === 'string' && selectedEditable.resource) {
    resource = selectedEditable.resource;
  } else {
    const idx = editables.findIndex((e) => e && e.id === selectedId);
    for (let i = idx - 1; i >= 0; i--) {
      const r = editables[i]?.resource;
      if (typeof r === 'string' && r) {
        resource = r;
        resourceEditable = editables[i];
        break;
      }
    }
  }

  return {
    selectedId,
    resource,                            // raw, e.g. `urn:aemconnection:/content/...`
    cleanResource: stripResourceUrn(resource), // e.g. `/content/...` (what AEM Sling POSTs expect)
    label,
    imageLike: isImageLikeResource(selectedEditable),
    editable: resourceEditable,          // the Editable object whose resource we're targeting
  };
}

const EMPTY_SELECTION = { selectedId: '', resource: '', cleanResource: '', label: '', imageLike: false, editable: null };

// Derive the AEM repository hostname from the author URL — that's what the
// AEM Assets Selector wants as `repositoryId` (e.g. `author-p12345-e67890.adobeaemcloud.com`).
function repositoryIdFromAuthorUrl(authorUrl) {
  if (typeof authorUrl !== 'string' || !authorUrl) return '';
  try {
    return new URL(authorUrl).host;
  } catch (_) {
    return authorUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  }
}

const INITIAL_STATE = {
  ready: false,
  error: '',
  conn: null,
  imsToken: '',
  orgId: '',
  authorUrl: '',
  repositoryId: '',
  sitePath: '',
  siteSlug: '',
  damFolderPath: '',
  pagePath: '',
  pagesRoot: '',
  selection: EMPTY_SELECTION,
};

export default function useUEContext() {
  const [state, setState] = useState(INITIAL_STATE);
  const connRef = useRef(null);
  const lastSelectedIdRef = useRef('');
  const lastEditablesFingerprintRef = useRef('');
  const firstDumpRef = useRef(false);
  const tickCountRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    let pollHandle = null;

    const patch = (partial) => {
      if (!mounted) return;
      setState((prev) => ({ ...prev, ...partial }));
    };

    const attachWithRetry = async () => {
      let lastErr;
      for (let i = 0; i < ATTACH_RETRIES; i++) {
        try {
          const c = await attach({ id: extensionId });
          console.log('[DemoPilot] attach() ok on try', i + 1);
          return c;
        } catch (err) {
          lastErr = err;
          console.warn('[DemoPilot] attach() try', i + 1, 'failed:', err && (err.message || err));
          await new Promise((r) => setTimeout(r, ATTACH_RETRY_DELAY_MS));
        }
      }
      throw lastErr || new Error('attach() failed');
    };

    const probeSharedContext = (conn) => {
      const sc = conn?.sharedContext;
      // Try every key the reference + docs mention, plus a couple extras.
      const keys = ['auth', 'token', 'orgId', 'imsOrg', 'imsToken', 'aemHost', 'authScheme', 'locale', 'theme'];
      const out = {};
      if (!sc || typeof sc.get !== 'function') return { hasSharedContext: false, probes: out };
      for (const k of keys) {
        try {
          out[k] = sc.get(k);
        } catch (e) {
          out[k] = `<threw: ${e && e.message}>`;
        }
      }
      return { hasSharedContext: true, probes: out };
    };

    const readAuthAndHost = (conn) => {
      const sc = conn?.sharedContext;
      const scAuth = sc?.get?.('auth');
      const imsToken = scAuth?.imsToken || sc?.get?.('token') || sc?.get?.('imsToken') || '';
      const orgId = scAuth?.imsOrg || sc?.get?.('orgId') || sc?.get?.('imsOrg') || '';
      const aemHostHint = sc?.get?.('aemHost') || '';
      return { imsToken, orgId, aemHostHint };
    };

    const tick = async () => {
      const conn = connRef.current;
      if (!conn) return;
      tickCountRef.current += 1;
      const tickN = tickCountRef.current;

      let editorState;
      try {
        editorState = await conn.host.editorState.get();
      } catch (err) {
        console.warn('[DemoPilot] tick #' + tickN + ' editorState.get() failed:', err && (err.message || err));
        if (!firstDumpRef.current) {
          firstDumpRef.current = true;
          const { hasSharedContext, probes } = probeSharedContext(conn);
          console.log('[DemoPilot] first-tick diagnostic (editorState threw):', {
            hasSharedContext,
            sharedContextProbes: probes,
            connKeys: conn ? Object.keys(conn) : null,
            hostKeys: conn?.host ? Object.keys(conn.host) : null,
          });
        }
        if (!mounted) return;
        patch({ ready: false, error: 'editorState.get() failed: ' + (err && err.message) });
        return;
      }

      // First-tick raw dump — one-shot, so we can see what UE actually gives us.
      if (!firstDumpRef.current) {
        firstDumpRef.current = true;
        const { hasSharedContext, probes } = probeSharedContext(conn);
        console.log('[DemoPilot] first-tick raw editorState:', editorState);
        console.log('[DemoPilot] first-tick sharedContext:', {
          hasSharedContext,
          probes,
        });
        console.log('[DemoPilot] first-tick conn shape:', {
          connKeys: conn ? Object.keys(conn) : null,
          hostKeys: conn?.host ? Object.keys(conn.host) : null,
          sharedContextKeys:
            conn?.sharedContext && typeof conn.sharedContext === 'object'
              ? Object.keys(conn.sharedContext)
              : null,
        });
        // Deep-probe every namespace under conn.host — we're hunting for a
        // method that triggers `aue:content-patch` on the canvas page so we
        // can refresh the affected element in place without refreshPage().
        try {
          const hostObj = conn?.host || {};
          const deep = {};
          for (const ns of Object.keys(hostObj)) {
            try {
              const v = hostObj[ns];
              deep[ns] = (v && typeof v === 'object') ? Object.keys(v) : typeof v;
            } catch (e) {
              deep[ns] = `<threw: ${e && e.message}>`;
            }
          }
          console.log('[DemoPilot] first-tick host namespaces (deep):', deep);
        } catch (e) {
          console.warn('[DemoPilot] deep host probe failed:', e);
        }
      }

      // Author URL (refresh each tick — it rarely changes but is cheap)
      const fromConns = pickAuthorUrlFromConnections(editorState?.connections);
      const { imsToken, orgId, aemHostHint } = readAuthAndHost(conn);
      const authorUrl = normalizeAuthorUrl(fromConns || aemHostHint);
      const repositoryId = repositoryIdFromAuthorUrl(authorUrl);

      // Site slug inference — prefer current selection's resource, then any
      // editable's resource, then the editor's location URL as a final fallback.
      const selection = readSelectionFromState(editorState);
      const slugFromSelection = siteSlugFromResource(selection.resource);
      const slugFromEditables = siteSlugFromEditables(editorState?.editables);
      const slugFromLocation = siteSlugFromLocation(editorState?.location);
      const siteSlug = slugFromSelection || slugFromEditables || slugFromLocation || '';
      const sitePath = siteSlug ? `/content/${siteSlug}` : '';
      const damFolderPath = damFolderFromSiteSlug(siteSlug);

      // Derive pagePath and pagesRoot from the editor location URL and editables.
      // pagePath = the JCR path of the currently edited page.
      // pagesRoot = the parent folder containing sibling pages (language root).
      const pagePath = pagePathFromLocation(editorState?.location);
      const pagesRootFromLoc = pagesRootFromPagePath(pagePath);
      const pagesRootFromEds = pagesRootFromEditables(editorState?.editables);
      const pagesRoot = pagesRootFromLoc || pagesRootFromEds || '';

      // Cheap change detection. Include `ready` so we always patch the first time.
      const selFingerprint = `${selection.selectedId}|${selection.resource}`;
      const fingerprint = `${selFingerprint}|${siteSlug}|${authorUrl}|${imsToken ? '1' : '0'}|${orgId}|${pagesRoot}`;
      if (fingerprint === lastEditablesFingerprintRef.current) return;
      lastEditablesFingerprintRef.current = fingerprint;
      if (selection.selectedId !== lastSelectedIdRef.current) {
        lastSelectedIdRef.current = selection.selectedId;
      }

      // `ready` flips to true only once we have at least SOMETHING real.
      // Author URL or IMS token is a good signal — means UE populated the context.
      const readyNow = !!(authorUrl || imsToken || siteSlug);

      console.log('[DemoPilot] tick #' + tickN + ' resolved', {
        readyNow,
        hasToken: !!imsToken,
        orgId,
        authorUrl,
        repositoryId,
        siteSlug,
        sitePath,
        damFolderPath,
        pagePath,
        pagesRoot,
        selection,
      });

      patch({
        ready: readyNow,
        error: '',
        conn,
        imsToken,
        orgId,
        authorUrl,
        repositoryId,
        siteSlug,
        sitePath,
        damFolderPath,
        pagePath,
        pagesRoot,
        selection,
      });
    };

    (async () => {
      try {
        const conn = await attachWithRetry();
        if (!mounted) return;
        connRef.current = conn;
        // Don't flip ready yet — wait until first tick actually populates data.
        patch({ conn });
        await tick();
        pollHandle = setInterval(tick, POLL_MS);
      } catch (err) {
        patch({
          ready: false,
          error:
            'Cannot connect to Universal Editor. Confirm devMode/ext is enabled and the local HTTPS cert is trusted.',
        });
        console.warn('[DemoPilot] attach() failed after retries:', err);
      }
    })();

    return () => {
      mounted = false;
      if (pollHandle) clearInterval(pollHandle);
    };
  }, []);

  return state;
}
